import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { analytics, EVENTS } from '../../lib/analytics'
import {
  Box, Card, CardContent, Typography, Button, Checkbox, CircularProgress, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, Chip, Grid,
} from '@mui/material'
import { CompareArrows } from '@mui/icons-material'
import { MapContainer, Polyline } from 'react-leaflet'
import MapTileLayer from '../../components/MapTileLayer'
import 'leaflet/dist/leaflet.css'
import { useQuery } from '@tanstack/react-query'
import { PageHeader } from '../../components/PageHeader'
import { useTrips, useTripComparison, Trip } from '../../hooks/useFleet'
import { useDevices, Device } from '../../hooks/useDevices'
import { api } from '../../lib/api'
import { fmtDateTimeFull } from '../../lib/dateFormat'

const COLORS = ['#34A8E0', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6']

interface GpsPoint {
  latitude: number
  longitude: number
  speed: number
  heading: number
  time: string
}

function unwrap<T>(d: unknown): T[] {
  if (Array.isArray(d)) return d as T[]
  const o = d as { devices?: T[] } | undefined
  return o?.devices ?? []
}

function formatDuration(sec: number): string {
  if (sec < 60) return `${sec}s`
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

export default function TripComparisonPage() {
  const { t } = useTranslation()
  const { data: tripsRaw = [], isLoading: tripsLoading } = useTrips()
  const { data: devicesRaw } = useDevices()
  const devices = unwrap<Device>(devicesRaw)

  const deviceNames = useMemo(() => {
    const map = new Map<string, string>()
    devices.forEach((d) => map.set(d.id, d.name))
    return map
  }, [devices])

  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const toggleTrip = (id: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id)
      if (prev.length >= 5) return prev
      const next = [...prev, id]
      if (next.length >= 2) {
        analytics.track(EVENTS.TRIP_COMPARED, { trip_count: next.length, trip_ids: next })
      }
      return next
    })
  }

  const { data: comparedTrips = [] } = useTripComparison(selectedIds)

  // Fetch GPS tracks for selected trips
  const trackQueries = useQuery({
    queryKey: ['trip-comparison-tracks', selectedIds],
    queryFn: async () => {
      const results: { tripId: string; points: GpsPoint[] }[] = []
      // Use the trips from the list to get device_id and time range
      for (const id of selectedIds) {
        const trip = tripsRaw.find((t) => t.id === id)
        if (!trip) continue
        try {
          const r = await api.get('/analytics/gps/track', {
            params: {
              device_id: trip.device_id,
              from: trip.started_at,
              to: trip.ended_at || new Date().toISOString(),
            },
          })
          const d = r.data
          const points: GpsPoint[] = Array.isArray(d) ? d : (d as any)?.points ?? []
          results.push({ tripId: id, points })
        } catch {
          results.push({ tripId: id, points: [] })
        }
      }
      return results
    },
    enabled: selectedIds.length >= 2,
  })

  const tracks = trackQueries.data ?? []

  // Use compared trips if available, else fall back to local trips
  const displayTrips: Trip[] = comparedTrips.length > 0
    ? comparedTrips
    : tripsRaw.filter((t) => selectedIds.includes(t.id))

  // Compute map center from all track points
  const mapCenter = useMemo<[number, number]>(() => {
    for (const track of tracks) {
      if (track.points.length > 0) {
        return [track.points[0].latitude, track.points[0].longitude]
      }
    }
    return [33.3152, 44.3661]
  }, [tracks])

  return (
    <Box>
      <PageHeader
        title={t('trips.comparison')}
        subtitle={t('trips.selectTripsToCompare')}
      />

      {/* Trip selector table */}
      <Card sx={{ mb: 3, border: '1px solid #e2e8f0' }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            {t('trips.title')} ({tripsRaw.length})
          </Typography>
          {tripsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 600, fontSize: 12 } }}>
                    <TableCell padding="checkbox" />
                    <TableCell>{t('common.name')}</TableCell>
                    <TableCell>{t('trips.start')}</TableCell>
                    <TableCell>{t('trips.distance')}</TableCell>
                    <TableCell>{t('trips.duration')}</TableCell>
                    <TableCell>{t('common.status')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tripsRaw.slice(0, 50).map((trip) => {
                    const checked = selectedIds.includes(trip.id)
                    const disabled = !checked && selectedIds.length >= 5
                    return (
                      <TableRow
                        key={trip.id}
                        hover
                        onClick={() => !disabled && toggleTrip(trip.id)}
                        sx={{ cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1 }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={checked} disabled={disabled} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>
                          {deviceNames.get(trip.device_id) ?? trip.device_id.slice(0, 8)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{fmtDateTimeFull(trip.started_at)}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{trip.distance_km.toFixed(1)} km</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{formatDuration(trip.duration_sec)}</TableCell>
                        <TableCell>
                          <Chip
                            label={trip.status}
                            size="small"
                            sx={{
                              fontSize: 11,
                              bgcolor: trip.status === 'completed' ? '#f0fdf4' : '#eff6ff',
                              color: trip.status === 'completed' ? '#10b981' : '#3b82f6',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {selectedIds.length < 2 && (
        <Alert severity="info" icon={<CompareArrows />} sx={{ mb: 3 }}>
          {t('trips.selectTripsToCompare')}
        </Alert>
      )}

      {selectedIds.length >= 2 && (
        <>
          {/* Metrics comparison table */}
          <Card sx={{ mb: 3, border: '1px solid #e2e8f0' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('trips.comparison')}</Typography>
              <Box sx={{ overflow: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 12 } }}>
                      <TableCell>Metric</TableCell>
                      {displayTrips.map((trip, i) => (
                        <TableCell key={trip.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COLORS[i % COLORS.length] }} />
                            {deviceNames.get(trip.device_id) ?? trip.device_id.slice(0, 8)}
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>{t('trips.distance')}</TableCell>
                      {displayTrips.map((trip) => (
                        <TableCell key={trip.id} sx={{ fontSize: 12 }}>{trip.distance_km.toFixed(2)} km</TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>{t('trips.duration')}</TableCell>
                      {displayTrips.map((trip) => (
                        <TableCell key={trip.id} sx={{ fontSize: 12 }}>{formatDuration(trip.duration_sec)}</TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>{t('trips.maxSpeed')}</TableCell>
                      {displayTrips.map((trip) => (
                        <TableCell key={trip.id} sx={{ fontSize: 12 }}>{trip.max_speed_kph.toFixed(0)} km/h</TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>{t('trips.avgSpeed')}</TableCell>
                      {displayTrips.map((trip) => (
                        <TableCell key={trip.id} sx={{ fontSize: 12 }}>{trip.avg_speed_kph.toFixed(1)} km/h</TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>{t('common.status')}</TableCell>
                      {displayTrips.map((trip) => (
                        <TableCell key={trip.id} sx={{ fontSize: 12 }}>
                          <Chip label={trip.status} size="small" sx={{ fontSize: 10 }} />
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>

          {/* Map with overlaid GPS tracks */}
          <Card sx={{ border: '1px solid #e2e8f0' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>GPS Tracks</Typography>
              {trackQueries.isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ height: 450, borderRadius: 1, overflow: 'hidden' }}>
                  <MapContainer center={mapCenter} zoom={13} style={{ height: '100%', width: '100%' }}>
                    <MapTileLayer />
                    {tracks.map((track, i) => {
                      if (track.points.length < 2) return null
                      const positions: [number, number][] = track.points.map((p) => [p.latitude, p.longitude])
                      return (
                        <Polyline
                          key={track.tripId}
                          positions={positions}
                          pathOptions={{ color: COLORS[i % COLORS.length], weight: 3 }}
                        />
                      )
                    })}
                  </MapContainer>
                </Box>
              )}
              {/* Legend */}
              <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                {selectedIds.map((id, i) => {
                  const trip = tripsRaw.find((t) => t.id === id)
                  return (
                    <Box key={id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 3, bgcolor: COLORS[i % COLORS.length], borderRadius: 1 }} />
                      <Typography variant="caption" color="text.secondary">
                        {trip ? (deviceNames.get(trip.device_id) ?? trip.device_id.slice(0, 8)) : id.slice(0, 8)}
                      </Typography>
                    </Box>
                  )
                })}
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  )
}
